import { useEffect, useRef } from "react";
import {
  advanceRace,
  buildTrack,
  fmtLap,
  newRaceState,
  type Track,
} from "./raceTrack";
import { armAudio, setHum, stopHum, tick, ding, fanfare, voidBlip, thud } from "./raceAudio";
import { logbook } from "../logbook";

/**
 * Race mode: the page dims, the cursor becomes a little quad, six arrow
 * flags and a checkered gate appear with the low-opacity race line looping
 * through them. Everything per-frame lives in refs and direct canvas/DOM
 * writes (off-React rule); React renders this shell exactly once.
 */
export function RaceMode({ onExit }: { onExit: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lapRef = useRef<HTMLElement>(null);
  const timeRef = useRef<HTMLElement>(null);
  const lastRef = useRef<HTMLElement>(null);
  const bestRef = useRef<HTMLElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  useEffect(() => {
    armAudio();
    const cv = canvasRef.current!;
    const c = cv.getContext("2d")!;
    let W = 0, H = 0, dpr = 1;
    let track: Track = buildTrack(window.innerWidth, window.innerHeight);
    let glow: HTMLCanvasElement | null = null;

    /**
     * The race line's soft corridor, rendered ONCE per resize into its own
     * bitmap: a stack of blurred strokes from wide-and-faint to narrow-and-
     * brighter, so the band feathers out into the dark instead of ending on
     * a hard edge. Blitted each frame, so the feathering is free.
     */
    const buildGlow = () => {
      const g = document.createElement("canvas");
      g.width = Math.max(1, Math.round(W * dpr));
      g.height = Math.max(1, Math.round(H * dpr));
      const gc = g.getContext("2d");
      if (!gc) return null;
      gc.setTransform(dpr, 0, 0, dpr, 0, 0);
      gc.lineJoin = "round";
      gc.lineCap = "round";
      const path = new Path2D();
      const P = track.poly;
      path.moveTo(P[0].x, P[0].y);
      for (let i = 1; i < P.length; i++) path.lineTo(P[i].x, P[i].y);
      path.closePath();
      // width, alpha, blur radius
      const passes: [number, number, number][] = [
        [50, 0.028, 14],
        [36, 0.036, 10],
        [25, 0.046, 7],
        [15, 0.058, 4],
      ];
      for (const [lw, a, b] of passes) {
        gc.filter = "blur(" + b + "px)"; // ignored where unsupported: the
        gc.strokeStyle = "rgba(120,255,170," + a + ")"; // stacked widths still
        gc.lineWidth = lw; // read as a soft gradient
        gc.stroke(path);
      }
      gc.filter = "none";
      return g;
    };

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = W * dpr;
      cv.height = H * dpr;
      cv.style.width = W + "px";
      cv.style.height = H + "px";
      track = buildTrack(W, H);
      glow = buildGlow();
    };
    resize();
    window.addEventListener("resize", resize);

    const st = newRaceState();
    const pos = { x: track.gate.x, y: track.gate.y + 70 };
    const tgt = { ...pos };
    let vx = 0, vy = 0;
    let heading = -Math.PI / 2;
    const trail: { x: number; y: number }[] = [];
    let lastLap: number | null = null;
    let best = logbook.state().best;
    let sessionLaps = 0;
    let crashT = -1e9;
    let bestT = -1e9;

    const onMove = (e: PointerEvent) => {
      tgt.x = e.clientX;
      tgt.y = e.clientY;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitRef.current();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onMove);
    window.addEventListener("keydown", onKey);

    const draw = (now: number) => {
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.clearRect(0, 0, W, H);
      const P = track.poly;

      // the race line: the feathered corridor (pre-rendered) + a flowing
      // dashed core, whose motion shows the travel direction
      if (glow) c.drawImage(glow, 0, 0, W, H);
      c.beginPath();
      c.moveTo(P[0].x, P[0].y);
      for (let i = 1; i < P.length; i++) c.lineTo(P[i].x, P[i].y);
      c.closePath();
      c.lineJoin = "round";
      c.strokeStyle = "rgba(140,255,185,0.32)";
      c.lineWidth = 2;
      c.setLineDash([9, 15]);
      c.lineDashOffset = -((now * 0.05) % 24);
      c.stroke();
      c.setLineDash([]);

      // arrow flags
      for (let i = 0; i < track.flags.length; i++) {
        const f = track.flags[i];
        const cleared = i < st.expected;
        if (i === st.expected) {
          const rr = track.captureR * (0.5 + 0.07 * Math.sin(now * 0.006));
          c.strokeStyle = "rgba(255,220,120,0.18)";
          c.lineWidth = 2;
          c.beginPath();
          c.arc(f.x, f.y, rr, 0, 7);
          c.stroke();
          // the arc thickens on the side you must pass: always the outside
          // of the bend, because the flag sits at its centre
          const a0 = Math.atan2(f.dirX * f.req, -f.dirY * f.req);
          c.strokeStyle = "rgba(255,214,110,0.62)";
          c.lineWidth = 3.5;
          c.beginPath();
          c.arc(f.x, f.y, rr, a0 - 0.85, a0 + 0.85);
          c.stroke();
        }
        c.strokeStyle = cleared ? "#9fe8c0" : "#cfd3dd";
        c.lineWidth = 2;
        c.lineCap = "round";
        c.beginPath();
        c.moveTo(f.x, f.y + 16);
        c.lineTo(f.x, f.y - 20);
        c.stroke();
        c.fillStyle = cleared ? "#37d67a" : "#d22c2c";
        c.beginPath();
        c.moveTo(f.x, f.y - 20);
        c.lineTo(f.x + 17, f.y - 12.5);
        c.lineTo(f.x, f.y - 5);
        c.closePath();
        c.fill();
        c.strokeStyle = "#ffd23f";
        c.lineWidth = 1.6;
        c.beginPath();
        c.moveTo(f.x + 3.5, f.y - 16);
        c.lineTo(f.x + 8.5, f.y - 12.5);
        c.lineTo(f.x + 3.5, f.y - 9);
        c.stroke();
      }

      // the start/finish gate: dashed line + two checkered poles
      const g = track.gate;
      const p1 = { x: g.x - g.dirY * g.half, y: g.y + g.dirX * g.half };
      const p2 = { x: g.x + g.dirY * g.half, y: g.y - g.dirX * g.half };
      c.strokeStyle = "rgba(255,255,255,0.28)";
      c.lineWidth = 1.5;
      c.setLineDash([4, 6]);
      c.beginPath();
      c.moveTo(p1.x, p1.y);
      c.lineTo(p2.x, p2.y);
      c.stroke();
      c.setLineDash([]);
      for (const p of [p1, p2]) {
        c.strokeStyle = "#cfd3dd";
        c.lineWidth = 2;
        c.beginPath();
        c.moveTo(p.x, p.y + 14);
        c.lineTo(p.x, p.y - 27);
        c.stroke();
        for (let ry = 0; ry < 3; ry++)
          for (let rx = 0; rx < 4; rx++) {
            c.fillStyle = (rx + ry) % 2 ? "#e8ecf4" : "#14161d";
            c.fillRect(p.x + rx * 5, p.y - 27 + ry * 5, 5, 5);
          }
      }

      // motion trail
      for (let i = 0; i < trail.length; i++) {
        c.fillStyle = "rgba(255,176,32," + ((i / trail.length) * 0.28).toFixed(3) + ")";
        c.beginPath();
        c.arc(trail[i].x, trail[i].y, 2, 0, 7);
        c.fill();
      }

      // the drone cursor: X frame, orange front motors, red rear, banking
      const sp = Math.hypot(vx, vy);
      if (sp > 60) {
        const want = Math.atan2(vy, vx);
        let dAng = want - heading;
        while (dAng > Math.PI) dAng -= Math.PI * 2;
        while (dAng < -Math.PI) dAng += Math.PI * 2;
        heading += dAng * 0.25;
      }
      const lean = Math.min(1, sp / 1600);
      c.save();
      c.translate(pos.x, pos.y);
      if (now - crashT < 380) c.translate((Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5);
      c.rotate(heading);
      c.scale(1 - lean * 0.28, 1);
      c.strokeStyle = "#39404f";
      c.lineWidth = 3;
      c.lineCap = "round";
      c.beginPath();
      c.moveTo(-9, -9);
      c.lineTo(9, 9);
      c.moveTo(9, -9);
      c.lineTo(-9, 9);
      c.stroke();
      c.shadowBlur = 9;
      for (const [ax, ay, col] of [
        [9, -9, "#ffb020"],
        [9, 9, "#ffb020"],
        [-9, -9, "#ff4545"],
        [-9, 9, "#ff4545"],
      ] as const) {
        c.shadowColor = col;
        c.fillStyle = col;
        c.beginPath();
        c.arc(ax, ay, 3, 0, 7);
        c.fill();
      }
      c.shadowBlur = 0;
      c.fillStyle = "#1d222d";
      c.beginPath();
      c.arc(0, 0, 2.6, 0, 7);
      c.fill();
      c.restore();

      // crash ring + new-best celebration
      if (now - crashT < 380) {
        const q = (now - crashT) / 380;
        c.strokeStyle = "rgba(255,70,60," + (0.5 * (1 - q)).toFixed(3) + ")";
        c.lineWidth = 2;
        c.beginPath();
        c.arc(pos.x, pos.y, 10 + q * 30, 0, 7);
        c.stroke();
      }
      if (now - bestT < 900) {
        const q = (now - bestT) / 900;
        c.strokeStyle = "rgba(255,210,90," + (0.5 * (1 - q)).toFixed(3) + ")";
        c.lineWidth = 3;
        c.beginPath();
        c.arc(g.x, g.y, 20 + q * 190, 0, 7);
        c.stroke();
      }
    };

    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
      last = now;
      const px = pos.x, py = pos.y;
      const k = Math.min(1, dt * 14);
      pos.x += (tgt.x - pos.x) * k;
      pos.y += (tgt.y - pos.y) * k;
      vx = (pos.x - px) / dt;
      vy = (pos.y - py) / dt;
      setHum(Math.min(1, Math.hypot(vx, vy) / 1500));

      trail.push({ x: pos.x, y: pos.y });
      if (trail.length > 16) trail.shift();

      for (const ev of advanceRace(track, st, px, py, pos.x, pos.y, now)) {
        if (ev.type === "flag") tick();
        else if (ev.type === "crash") {
          thud();
          crashT = now;
        } else if (ev.type === "start") {
          ding();
          if (hintRef.current)
            hintRef.current.textContent = "Clock armed. Six flags, in order, then back through the gate.";
        } else if (ev.type === "lap") {
          if (ev.valid) {
            sessionLaps++;
            lastLap = ev.ms;
            logbook.addLap(ev.ms);
            if (best === null || ev.ms < best) {
              best = ev.ms;
              bestT = now;
              fanfare();
            } else ding();
          } else {
            voidBlip();
            if (hintRef.current)
              hintRef.current.textContent = "Lap void: a flag was missed. The gate re-armed the clock.";
          }
        }
      }

      draw(now);
      if (lapRef.current) lapRef.current.textContent = String(sessionLaps);
      if (timeRef.current)
        timeRef.current.textContent = st.lapStart === null ? "--" : fmtLap(now - st.lapStart);
      if (lastRef.current) lastRef.current.textContent = lastLap === null ? "--" : fmtLap(lastLap);
      if (bestRef.current) bestRef.current.textContent = best === null ? "--" : fmtLap(best);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove);
      window.removeEventListener("keydown", onKey);
      stopHum();
    };
  }, []);

  return (
    <div className="race-layer">
      <canvas ref={canvasRef} className="race-canvas" />
      <div className="race-hud">
        <span className="rh">
          LAPS <b ref={lapRef}>0</b>
        </span>
        <span className="rh">
          TIME <b ref={timeRef}>--</b>
        </span>
        <span className="rh">
          LAST <b ref={lastRef}>--</b>
        </span>
        <span className="rh">
          BEST <b ref={bestRef}>--</b>
        </span>
      </div>
      <button className="race-exit" onClick={onExit}>
        exit race
      </button>
      <div className="race-hint" ref={hintRef}>
        Fly through the checkered gate to arm the clock. Keep every flag on the line side, in order.
      </div>
    </div>
  );
}
